import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  signOut,
  onAuthStateChanged,
  signInWithCustomToken,
} from 'firebase/auth';
import { auth, trackEvent } from 'app/javascript/firebase';
import { ROUTES } from 'app/javascript/constants/routes';
import { apiFetch, parseApiError } from 'app/javascript/services/api';

// Extended User type including Rails data
export interface RailsUser extends FirebaseUser {
  railsId?: number;
  slug?: string;
  role?: string;
  name?: string;
  nickname?: string;
  photoUrl?: string;
  birthday?: string;
  cpf?: string;
  guardianCpf?: string;
  hasDependents?: boolean;
  claimed?: boolean;
  currentBeltRank?: string;
  isBlackBelt?: boolean;
  appAdmin?: boolean;
}

// Web sessions belong to the app owner only: students and academies sign in
// through the native app. Sign-in here is the e-mail code, and a session whose
// /me is not app_admin is dropped as soon as it syncs.
interface AuthContextType {
  currentUser: RailsUser | null;
  loading: boolean;
  requestEmailCode: (email: string) => Promise<void>;
  verifyEmailCode: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

async function clearWebSession() {
  const response = await apiFetch(ROUTES.ADMIN_SESSION, { method: 'DELETE' });
  if (!response.ok) throw new Error('Falha ao encerrar a sessão web');
}

async function signOutWeb() {
  try {
    await clearWebSession();
  } catch (error) {
    console.error('Failed to clear web session:', error);
  } finally {
    await signOut(auth);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<RailsUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Sync with Rails backend
        try {
          // We need to wait a bit for the token to be ready or just use the user object directly
          // apiFetch internally gets the token from auth.currentUser
          const response = await apiFetch('/api/v1/me');

          if (response.ok) {
            const railsData = await response.json();
            if (!railsData.app_admin) {
              // Not the app owner: the web has no session for this account.
              await signOutWeb();
              setCurrentUser(null);
              setLoading(false);
              return;
            }
            const webSession = await apiFetch(ROUTES.ADMIN_SESSION, { method: 'POST' });
            if (!webSession.ok) throw new Error('Falha ao iniciar a sessão web');
            const syncedUser: RailsUser = {
              ...user,
              railsId: railsData.id,
              slug: railsData.slug,
              role: railsData.role,
              name: railsData.name,
              nickname: railsData.nickname,
              photoUrl: railsData.photo_url,
              birthday: railsData.birthday,
              cpf: railsData.cpf,
              // Verified link (guardian.cpf) wins over a pending guardian_cpf.
              guardianCpf: railsData.guardian?.cpf ?? railsData.guardian_cpf,
              hasDependents: railsData.has_dependents,
              claimed: railsData.claimed,
              currentBeltRank: railsData.current_belt_rank,
              isBlackBelt: railsData.is_black_belt,
              appAdmin: railsData.app_admin,
            };
            setCurrentUser(syncedUser);
            trackEvent('login_completed');
          } else {
            // Without a confirmed app_admin there is no web session to keep:
            // a failed sync must not leave a Firebase user signed in here.
            console.error('Failed to sync user with backend');
            await signOutWeb();
            setCurrentUser(null);
          }
        } catch (error) {
          console.error('Error syncing user:', error);
          await signOutWeb();
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const requestEmailCode = async (email: string) => {
    const response = await apiFetch('/api/v1/auth/email_code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Falha ao enviar código de acesso'));
    }
  };

  const verifyEmailCode = async (email: string, code: string) => {
    const response = await apiFetch('/api/v1/auth/email_code/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });

    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Código inválido ou expirado'));
    }

    const data = await response.json();
    await signInWithCustomToken(auth, data.custom_token);
  };

  const logout = async () => {
    await signOutWeb();
    setCurrentUser(null);
  };

  const refreshUser = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const response = await apiFetch('/api/v1/me');
      if (response.ok) {
        const railsData = await response.json();
        if (!railsData.app_admin) {
          await signOutWeb();
          setCurrentUser(null);
          return;
        }
        const syncedUser: RailsUser = {
          ...user,
          railsId: railsData.id,
          slug: railsData.slug,
          role: railsData.role,
          name: railsData.name,
          nickname: railsData.nickname,
          photoUrl: railsData.photo_url,
          birthday: railsData.birthday,
          cpf: railsData.cpf,
          // Verified link (guardian.cpf) wins over a pending guardian_cpf.
          guardianCpf: railsData.guardian?.cpf ?? railsData.guardian_cpf,
          hasDependents: railsData.has_dependents,
          claimed: railsData.claimed,
          currentBeltRank: railsData.current_belt_rank,
          isBlackBelt: railsData.is_black_belt,
          appAdmin: railsData.app_admin,
        };
        setCurrentUser(syncedUser);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const value = {
    currentUser,
    loading,
    requestEmailCode,
    verifyEmailCode,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
